// Phase NEL-B4 — Break-even anchor + financial-framing guards.
//
// Pure TypeScript, zero I/O. NEL_Logic §8: the mechanic has direct
// financial implications the system must surface HONESTLY. Two
// pieces:
//   1. computeBreakEvenAnchor — honest asymmetry framing (the same
//      capital-protection principle as SINGLES CHAIN). It is NOT a
//      guarantee and never encourages staking more.
//   2. The §8.3 prohibited-framing guard — a hard lint so no NEL
//      copy can ever imply win probability / safety / guarantee.
//
// Compounded detection confidence measures how thoroughly the
// extractor interrogated the slip — NOT the probability the slip
// wins. The system never makes outcome predictions.

export interface BreakEvenInput {
  /** User's declared per-slip stake (bankroll advisory or session). */
  stake: number;
  /** Combined decimal odds of the SEALED kept slip. */
  combinedOdds: number;
  /** Slips staked per week (default 5 — the §8.2 worked example). */
  slipsPerWeek?: number;
  /** Currency symbol for display only. Default "GH₵". */
  currency?: string;
}

export interface BreakEvenAnchor {
  returnAmount: number;
  weeklyOutlay: number;
  /** How many weeks of staking ONE landing covers. */
  weeksCovered: number;
  /** Honest, guarantee-free statement (passes the §8.3 guard). */
  statement: string;
}

function fmt(n: number): string {
  return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

/** §8.2 — "one landing covers N weeks" anchor. Honest asymmetry,
 *  never a guarantee. Returns null when inputs are unusable (no
 *  declared stake / degenerate odds) so the caller simply omits the
 *  anchor rather than showing nonsense. Pure. */
export function computeBreakEvenAnchor(
  input: BreakEvenInput,
): BreakEvenAnchor | null {
  const stake = Number(input.stake);
  const odds = Number(input.combinedOdds);
  const spw = input.slipsPerWeek && input.slipsPerWeek > 0
    ? input.slipsPerWeek
    : 5;
  const cur = input.currency ?? "GH₵";
  if (!Number.isFinite(stake) || stake <= 0) return null;
  if (!Number.isFinite(odds) || odds <= 1) return null;

  const returnAmount = stake * odds;
  const weeklyOutlay = stake * spw;
  const weeksCovered = weeklyOutlay > 0
    ? Math.floor(returnAmount / weeklyOutlay)
    : 0;

  const statement =
    `One landing on this slip returns ${cur}${fmt(returnAmount)}. ` +
    `At ${cur}${fmt(stake)} per slip × ${spw} slips per week = ` +
    `${cur}${fmt(weeklyOutlay)} weekly outlay. A single landing covers ` +
    `${fmt(weeksCovered)} week${weeksCovered === 1 ? "" : "s"} of staking. ` +
    `This is asymmetry context, not a prediction — nothing here says the ` +
    `slip will land.`;

  return { returnAmount, weeklyOutlay, weeksCovered, statement };
}

// §8.3 — prohibited financial framing. Any copy the NEL surface
// renders must pass this guard. Patterns are intentionally broad:
// false positives are cheap, a leaked guarantee is not.
const PROHIBITED_PATTERNS: { id: string; re: RegExp }[] = [
  { id: "win_chance", re: /\b(high|good|strong)\s+chance\s+of\s+winning\b/i },
  { id: "expect_landing", re: /\bexpect\s+(a\s+)?(landing|win|to\s+win|payout)\b/i },
  { id: "nearly_guaranteed", re: /\b(nearly|almost|practically)\s+guaranteed\b/i },
  { id: "guarantee", re: /\bguarantee(d|s)?\b/i },
  { id: "safe_bet", re: /\bsafe\s+bet\b/i },
  { id: "sure_thing", re: /\b(sure\s+thing|can'?t\s+lose|lock|banker)\b/i },
  { id: "will_win", re: /\b(will|going\s+to)\s+win\b/i },
];

/** Returns the ids of any prohibited-framing patterns the text
 *  trips. Empty array ⇒ the copy is honest. Pure. */
export function findProhibitedFraming(text: string): string[] {
  const t = String(text ?? "");
  return PROHIBITED_PATTERNS.filter((p) => p.re.test(t)).map((p) => p.id);
}

/** Hard guard: throws if NEL copy contains prohibited framing. Use
 *  in tests / dev to assert no surface can leak a guarantee. */
export function assertNoProhibitedFraming(text: string): void {
  const hits = findProhibitedFraming(text);
  if (hits.length > 0) {
    throw new Error(
      `Prohibited NEL financial framing (N-NEL §8.3): ${hits.join(", ")}`,
    );
  }
}
